import React, { FC } from 'react'
import styles from './styles.module.scss'

type DateProps = {
  placeholder: string
  value: string
  onChange: (value: string) => void
  onFocus: () => void
}

const DateInput: FC<DateProps> = ({
  placeholder,
  value,
  onChange,
  onFocus
}) => {
  const refs: {
    [key: string]: HTMLDivElement | null
  } = {
    main: null,
    date: null,
    month: null,
    year: null
  }

  const setInputRef = (name: string, element: HTMLDivElement | null) => {
    refs[name] = element
  }

  const updateElementClass = (
    element: HTMLDivElement | null,
    className: string,
    isRemove = false
  ) => {
    if (!element) {
      return
    }

    element.classList[isRemove ? 'remove' : 'add'](className)
  }

  const handleFocus = () => {
    onFocus()
    updateElementClass(refs.main, 'active')
  }

  const handleBlur = () => {
    updateElementClass(refs.main, 'active', true)
  }

  const getSubDate = (name: string, val: string) => {
    if (!name || !val) return ''

    const firstBreakIndex = val?.toString().indexOf('-')
    const lastBreakIndex = val?.toString().lastIndexOf('-')

    switch (name) {
      case 'year':
        return val?.toString()?.slice(0, firstBreakIndex) || ''
      case 'month':
        return val?.toString()?.slice(firstBreakIndex + 1, lastBreakIndex) || ''
      case 'date':
        return val?.toString()?.slice(lastBreakIndex + 1) || ''
      default:
        return ''
    }
  }

  const getFullDate = (name: string, val: string, prevVal: string) => {
    if (!name) return ''

    switch (name) {
      case 'year':
        return `${val}-${getSubDate('month', prevVal)}-${getSubDate(
          'date',
          prevVal
        )}`
      case 'month':
        return `${getSubDate('year', prevVal)}-${val}-${getSubDate(
          'date',
          prevVal
        )}`
      case 'date':
        return `${getSubDate('year', prevVal)}-${getSubDate(
          'month',
          prevVal
        )}-${val}`
      default:
        return ''
    }
  }

  const handleValueChanged = (name: string, val: string) => {
    const date = getSubDate('date', val)
    const month = getSubDate('month', val)
    const dateFilled = date.length === 2
    const monthFilled = month.length === 2

    if (monthFilled && name === 'month') {
      refs.date?.focus()
    } else if (dateFilled && name === 'date') {
      refs.year?.focus()
    }
  }

  const handleChange = ({
    target: { value: val = '', name }
  }: React.ChangeEvent<HTMLInputElement>): void => {
    let valueChanged = false

    const previousValue = getSubDate(name, value)
    let nextValue = val.replace(/[^\d]/g, '')

    if (name === 'date' && parseInt(nextValue, 10) > 31) {
      nextValue = previousValue
    } else if (name === 'month' && parseInt(nextValue, 10) > 12) {
      nextValue = previousValue
    }

    if (previousValue.length !== nextValue.length) valueChanged = true

    const fullValue = getFullDate(name, nextValue, value)
    onChange(fullValue)

    if (valueChanged) handleValueChanged(name, fullValue)
  }

  return (
    <>
      <div
        className={styles.input_container}
        ref={(node) => setInputRef('main', node)}
      >
        <input
          ref={(node) => setInputRef('month', node)}
          className={`${styles.input} ${styles.month}`}
          type="text"
          name="month"
          placeholder={getSubDate('month', placeholder)}
          value={getSubDate('month', value)}
          onChange={handleChange}
          maxLength={2}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
        <div className={styles.slash}>/</div>
        <input
          ref={(node) => setInputRef('date', node)}
          className={`${styles.input} ${styles.date}`}
          type="text"
          name="date"
          placeholder={getSubDate('date', placeholder)}
          value={getSubDate('date', value)}
          onChange={handleChange}
          maxLength={2}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
        <div className={styles.slash}>/</div>
        <input
          ref={(node) => setInputRef('year', node)}
          className={`${styles.input} ${styles.year}`}
          type="text"
          name="year"
          placeholder={getSubDate('year', placeholder)}
          value={getSubDate('year', value)}
          onChange={handleChange}
          maxLength={4}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
      </div>
    </>
  )
}

export default DateInput
